import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Icons } from "../../../Everou/_assets";
import { EmptyGenericIntegration } from "../../../Everou/_modelsTS/Integration";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { IntegrationConfigInput } from "./IntegrationConfigInput";
import { PMSLocationsActions } from "../../_actions/PMSLocationsActions";
import { removeFromArr } from "../../../Common/_utils/utils";
import { CapsuleButton } from "../../../Common/_components";
import { Selector } from "../../../Everou/_components";
import { integrationTemplateLabel } from "../../_helpers/IntegrationTemplateHelper";
import { isLocationInPersonalScope, resolveLocationOrganizationUid } from "../../_helpers/activeOrganization";
import { PMSIntegrationsModule } from "../../ModuleIntegrations/PMSIntegrationsModule";
import { PMSScopeOverrideStore } from "../../_stores/PMSScopeOverrideStore";
import { PMSNavigationActions } from "../../_actions/PMSNavigationActions";

export function IntegrationConfig() {

    // LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();

    // GLOBAL STATE
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    // Kept out of the shared integrations collection on purpose: this list
    // is the LOCATION's scope (resolveLocationOrganizationUid), which is not
    // necessarily the one the PMS accounts module shows.
    const [integrationTemplates, setIntegrationTemplates] = useState([]);
    const [templateLoadStatus, setTemplateLoadStatus] = useState("loading");
    const [templateReloadVersion, setTemplateReloadVersion] = useState(0);
    const [locationIntegrationsLoaded, setLocationIntegrationsLoaded] = useState(false);
    const [isTemplatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [newIntegrationTemplate, setNewIntegrationTemplate] = useState(null);
    // The PMS accounts module embedded here, in the location's scope:
    //  - "create": nothing to pick from, so the account is set up in place
    //    and, once saved, the add flow goes on with it (setting one up
    //    elsewhere and coming back was a dead end);
    //  - "browse": an existing account looked at (or one set up to assign
    //    to an orphaned integration), with a way back and no continuation.
    // While open, the module's catalog is watched for an account that did
    // not exist when it opened.
    const [creatorMode, setCreatorMode] = useState(null);
    const [catalogUidsAtOpen, setCatalogUidsAtOpen] = useState(null);
    // Reload version whose result should carry on into the add flow.
    const [continueAfterVersion, setContinueAfterVersion] = useState(null);
    // A location outside any organization, or of one the user is not a
    // member of: its accounts are the user's personal ones.
    const isPersonalScope = useSelector(isLocationInPersonalScope);
    const catalogTemplates = useSelector(Selectors.getPMSIntegrations);
    let locationIntegrations = [];
    locationIntegrations = useSelector(Selectors.getPMSLocationIntegrations);
    const [integrations, setIntegrations] = useState(buildIntegrations(locationIntegrations));

    const locationGroups = useSelector(Selectors.getPMSLocationGroups);

    // INITIALIZATION
    useEffect(() => {
        let cancelled = false;

        dispatch(PMSLocationsActions.getGroupsForLocation());
        setLocationIntegrationsLoaded(false);
        // Whatever was mid-flight belonged to the previous location.
        setTemplatePickerOpen(false);
        setNewIntegrationTemplate(null);
        setCreatorMode(null);
        setContinueAfterVersion(null);
        (async () => {
            await dispatch(PMSLocationsActions.getIntegrations());
            if (!cancelled) setLocationIntegrationsLoaded(true);
        })();

        return () => { cancelled = true; };
    }, [dispatch, locationUid]);

    useEffect(() => {
        let cancelled = false;

        // Drop the previous location's templates while the new ones load, so
        // the picker can't offer a scope that isn't the current one.
        setIntegrationTemplates([]);
        setTemplateLoadStatus("loading");
        (async () => {
            try {
                const templates = await dispatch(
                    PMSIntegrationsActions.listIntegrationTemplatesForLocation(),
                );
                if (cancelled) return;
                if (!Array.isArray(templates)) throw new Error("TEMPLATES_NOT_LOADED");
                setIntegrationTemplates(templates ?? []);
                setTemplateLoadStatus("loaded");
            } catch (_) {
                if (!cancelled) setTemplateLoadStatus("error");
            }
        })();

        return () => { cancelled = true; };
    }, [dispatch, locationUid, templateReloadVersion]);

    useEffect(() => {
        // Keep the row being added. The list is replaced wholesale on every
        // refresh, and that row exists only here, so anything refetching
        // mid-add used to take the half-filled form with it -- the sync
        // polling does exactly that, every 2.5s for up to 30s, from this very
        // screen. It is dropped explicitly instead, when it is saved
        // (onNewIntegrationCreated) or cancelled.
        setIntegrations(previous => [
            ...buildIntegrations(locationIntegrations),
            ...previous.filter(({ uid }) => !uid),
        ]);
    }, [locationIntegrations]);

    // The embedded module must work in the LOCATION's scope, whatever the
    // header has active -- the personal one included, which the active
    // organization alone can never express. Set BEFORE the module mounts:
    // its mount effect lists the catalog, and a child's effect runs before
    // the parent's. Cleared when the module goes, and on unmount.
    const openTemplateCreator = (mode) => {
        dispatch(PMSScopeOverrideStore.actionSet({
            organizationUid: resolveLocationOrganizationUid(store.getState()) ?? null,
        }));
        // Whatever the catalog holds now is at best the previous scope's:
        // none of it counts as created here (see the effect below).
        setCatalogUidsAtOpen(new Set(
            (Selectors.getPMSIntegrations(store.getState()) ?? []).map(({ uid }) => uid),
        ));
        setCreatorMode(mode);
    };
    useEffect(() => {
        if (!creatorMode) return;
        return () => { dispatch(PMSScopeOverrideStore.actionSet(null)); };
    }, [dispatch, creatorMode]);

    const closeTemplateCreator = () => {
        const shouldContinue = creatorMode === "create";
        setCreatorMode(null);
        setCatalogUidsAtOpen(null);
        // Whatever was done there (created, renamed, deleted) must show here:
        // a fresh fetch, with the status flipped now so the effects below
        // can't take the previous fetch's "loaded" for the new one.
        setTemplateLoadStatus("loading");
        setTemplateReloadVersion(version => version + 1);
        // Creating: go on with the new account once that fetch delivers it.
        // Browsing from an existing integration: back to it, the account is
        // assigned from its row (or adopted by the backend on its own).
        if (shouldContinue) setContinueAfterVersion(templateReloadVersion + 1);
    };

    // "Go to PMS account" on a location in the personal scope: personal
    // accounts have no screen of their own once the user has organizations,
    // so they are looked at right here, with the company's panel open.
    const browseAccount = (company = null, template = null, draft = null) => {
        dispatch(PMSNavigationActions.openIntegrationCompany(company, template, draft));
        openTemplateCreator("browse");
    };

    // An account saved in the embedded module shows up in its catalog under a
    // uid neither this location's list nor the catalog at opening time had:
    // that is the cue to close, no button to press. The location's list is
    // the same scope the module runs in (the override), so anything it knows
    // already existed.
    const knownTemplateUids = useMemo(
        () => new Set(integrationTemplates.map(({ uid }) => uid)),
        [integrationTemplates],
    );
    useEffect(() => {
        if (!creatorMode || templateLoadStatus !== "loaded") return;
        const hasNewTemplate = (catalogTemplates ?? []).some(
            ({ uid }) => uid && !knownTemplateUids.has(uid) && !catalogUidsAtOpen?.has(uid),
        );
        if (hasNewTemplate) closeTemplateCreator();
        // closeTemplateCreator is a plain closure over state setters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogTemplates, knownTemplateUids, catalogUidsAtOpen, creatorMode, templateLoadStatus]);

    useEffect(() => {
        if (
            continueAfterVersion === null ||
            templateReloadVersion !== continueAfterVersion ||
            templateLoadStatus !== "loaded"
        ) return;
        setContinueAfterVersion(null);
        if (availableTemplates.length > 0) startAddingIntegration();
        // Runs when that reload settles; the handlers read the fresh list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [continueAfterVersion, templateReloadVersion, templateLoadStatus]);

    const onDeleteIntegration = (index) => {
        const integration = integrations[index];
        if (!integration.uid) {
            // Unsaved row: nothing on the backend, just drop it locally.
            let updatedIntegrations = removeFromArr(index, integrations);
            setIntegrations(buildIntegrations(updatedIntegrations));
        } else {
            // Saved integration: confirm first (deleting it also removes its
            // reservations). The list re-syncs from Redux after the delete.
            dispatch(PMSLocationsActions.deleteLocationIntegrationUi(integration.uid));
        }
    };

    const startAddingIntegration = () => {
        if (availableTemplates.length === 1) {
            selectIntegrationTemplate(integrationTemplateOptions[0]);
            return;
        }
        setTemplatePickerOpen(true);
    };

    const selectIntegrationTemplate = (selectedTemplate) => {
        setNewIntegrationTemplate(selectedTemplate.template);
        setIntegrations([
            ...integrations,
            EmptyGenericIntegration,
        ]);
        setTemplatePickerOpen(false);
    };

    // The save landed: the server's copy is in the list now, so the local row
    // has to go or the form would linger next to the integration it created.
    const onNewIntegrationCreated = () => {
        onCancelNewIntegration();
    };

    const onCancelNewIntegration = () => {
        const index = integrations.findIndex(
            (integration) => !integration.uid,
        );
        if (index !== -1) {
            onDeleteIntegration(index);
            setNewIntegrationTemplate(null);
        }
    };

    const cancelAddingIntegration = () => {
        if (isTemplatePickerOpen) {
            setTemplatePickerOpen(false);
            return;
        }
        onCancelNewIntegration();
    };

    const hasUnsavedNewIntegration =
        integrations.some((integration) => !integration.uid);
    const isAddingIntegration = isTemplatePickerOpen || hasUnsavedNewIntegration;
    // In an organization's scope the listing also brings the manager's
    // personal (legacy) accounts, tagged isPersonal. The organization is what
    // governs there: offering personal ones only invites building
    // integrations the organization cannot manage -- which the backend
    // refuses anyway (error 793).
    //
    // Except the ones already governing an integration of THIS location: an
    // Enso Connect or Lodgify account mid-transfer stays personal until its
    // last location joins, and hiding it would leave its owner unable to see or
    // edit the account their reservations depend on. Only its owner sees it:
    // the personal listing is per-user.
    const usedTemplateUids = useMemo(
        () => new Set(integrations.map(({ templateUid }) => templateUid).filter(Boolean)),
        [integrations],
    );
    const availableTemplates = useMemo(
        () => integrationTemplates.filter(
            (template) => !template.isPersonal || usedTemplateUids.has(template.uid),
        ),
        [integrationTemplates, usedTemplateUids],
    );

    const templatesLoaded = templateLoadStatus === "loaded";
    const hasNoTemplates = templatesLoaded && availableTemplates.length === 0;
    // Nothing to pick AND nothing configured: straight into the catalog. With
    // integrations already here (orphaned by leaving an organization, say)
    // they stay on screen, with a way to set up the account to assign them.
    const needsFirstAccount = hasNoTemplates && integrations.length === 0;
    useEffect(() => {
        if (needsFirstAccount) openTemplateCreator("create");
        // openTemplateCreator is a plain closure over dispatch and state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsFirstAccount]);

    // Named, not just company: an owner may hold several accounts of one PMS --
    // and several Manual templates, which carry no credentials at all -- so the
    // company alone offers the same unreadable option twice.
    const integrationTemplateOptions = availableTemplates.map((template) => ({
        value: template.uid,
        label: template.isPersonal
            ? `${integrationTemplateLabel(template)} (${t("mod_pms_integration_template_personal")})`
            : integrationTemplateLabel(template),
        template,
    }));

    // The auto-open above lands one commit later: no empty state in between.
    if (needsFirstAccount && !creatorMode) return null;

    if (creatorMode) {
        return (
            <div>
                {creatorMode === "browse"
                    ? <div className="d-flex justify-content-end mb-2">
                        <CapsuleButton
                            text={t("global_back")}
                            onClick={closeTemplateCreator}
                            size={'SMALL'}
                        />
                    </div>
                    : <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-2">
                        <img
                            src={Icons.info}
                            alt="info"
                            className="mr-2 flex-shrink-0"
                            style={{ width: "24px", height: "24px" }}
                        />
                        <span>{t("mod_pms_location_integration_template_creator_info")}</span>
                    </div>
                }
                <PMSIntegrationsModule isEmbedded />
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-2">
                <img
                    src={Icons.info}
                    alt="info"
                    className="mr-2 flex-shrink-0"
                    style={{ width: "24px", height: "24px" }}
                />
                <span>
                    {t("mod_pms_location_integrations_info")}
                </span>
            </div>
            <div className="d-flex justify-content-end mb-2">
                {isTemplatePickerOpen &&
                    <div className="mr-2" style={{ width: "280px" }}>
                        <Selector
                            autoFocus
                            placeholder={t("mod_pms_select_pms_placeholder")}
                            value={null}
                            options={integrationTemplateOptions}
                            onChange={selectIntegrationTemplate}
                        />
                    </div>
                }
                {isAddingIntegration &&
                    <CapsuleButton
                        text={t("global_cancel")}
                        classNameExtra="mr-2"
                        // eslint-disable-next-line react/style-prop-object
                        style="DANGER"
                        onClick={cancelAddingIntegration}
                        size={'SMALL'}
                    />
                }
                {isPersonalScope && !isAddingIntegration &&
                    // Personal accounts have no screen of their own once the
                    // user has organizations: the locations using them are
                    // where they are managed.
                    <CapsuleButton
                        text={t("mod_pms_accounts_title")}
                        classNameExtra="mr-2"
                        // eslint-disable-next-line react/style-prop-object
                        style="INFO"
                        onClick={() => browseAccount()}
                        isEnabled={templatesLoaded}
                        size={'SMALL'}
                    />
                }
                {!isTemplatePickerOpen &&
                    <CapsuleButton
                        text={t("mod_pms_location_add_integration")}
                        icon={Icons.add}
                        onClick={startAddingIntegration}
                        isEnabled={
                            templatesLoaded &&
                            locationIntegrationsLoaded &&
                            availableTemplates.length > 0 &&
                            !hasUnsavedNewIntegration
                        }
                        size={'SMALL'}
                    />
                }
            </div>
            {templateLoadStatus === "error" &&
                <div className="d-flex align-items-center justify-content-between c-bg-grey-250 c-rounded-big p-3 mb-2">
                    <span className="text-danger">{t("global_error_network")}</span>
                    <CapsuleButton
                        text={t("global_retry")}
                        onClick={() => setTemplateReloadVersion(version => version + 1)}
                        size={'SMALL'}
                    />
                </div>
            }
            <div className="c-grid c-grid--small rounded-bottom">
                {integrations.length === 0 &&
                    <div className="text-center text-muted py-3">
                        <div>{t("mod_pms_location_no_integrations")}</div>
                    </div>
                }
                {integrations.map((integration, index) => (
                    <IntegrationConfigInput
                        key={integration.uid}
                        groups={locationGroups}
                        integrationTemplates={availableTemplates}
                        templatesLoaded={templatesLoaded}
                        index={index}
                        integration={integration}
                        initialTemplate={!integration.uid ? newIntegrationTemplate : undefined}
                        onCreated={onNewIntegrationCreated}
                        onBrowseAccount={isPersonalScope ? browseAccount : undefined}
                        onDelete={index => onDeleteIntegration(index)}
                    />
                ))}
            </div>
        </div>
    );


    function buildIntegrations(locationIntegrations) {
        return locationIntegrations ?? [];
    }

}
